import axios, { AxiosResponse } from 'axios'
import { signOut } from '@/lib/auth/signOut'
import { getAuthToken, hasJwtNotExpired } from '@/lib/auth/jwt'
import { trackLoginDebug } from '@/utils/loginDebug'
import { setRecoil } from 'recoil-nexus'
import { loginModalOpenState } from 'Store/login'
import {
  gemNotEnoughModalOpenState,
  newUserGemNotEnough,
  newUserGemNotEnoughModalOpenState,
  roleCardLimitModalOpenState,
  luckyEasterEggNotEnoughModalOpenState,
} from '@/store/chat'
import { chatUrls, roleCardUrls } from '@/constant/chat'
import { BASE_API_URL } from '@/constant/api'
import { USER_ID_HEADER, readUserId } from '@/constant/prefetch-user'
import { AppRespCode } from '@/types/api'
import { EASTER_CHAT_GIFT_CODES } from '@/constant/relationship'
import {
  Message,
  RelationshipLetterMessage,
  RelationshipMonologueMessage,
  RelationshipEventMessage,
  RelationshipRomanticMomentMessage,
  RelationshipTidalHeartsVideoMessage,
  RelationshipExclusiveMemoryMessage,
  RelationshipDreamMessage,
  RelationshipGemRefundMessage,
} from '@/types/message'
import { getRecoil } from 'recoil-nexus'
import { useRecoilValue } from 'recoil'
import { get } from 'lodash'
import { userState } from '@/store/user'
import { APP_ONLY_IMAGE_PLACEHOLDER } from '@/utils/message'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipLoginModalOn401?: boolean
  }
}

// 关系消息的类型别名
type RelationshipMessage =
  | RelationshipLetterMessage
  | RelationshipMonologueMessage
  | RelationshipEventMessage
  | RelationshipRomanticMomentMessage
  | RelationshipTidalHeartsVideoMessage
  | RelationshipExclusiveMemoryMessage
  | RelationshipDreamMessage
  | RelationshipGemRefundMessage

type HistoryImageMediaItem = {
  sequence?: string | number
}

type HistoryMessageWithUserImages = Message & {
  app_only_image_placeholder?: true
  image_media_items?: HistoryImageMediaItem[]
  original_image_urls?: string[]
  image_sequence?: string | number
}

// 支持的关系消息类型集合
const relationshipMessageTypes = new Set<RelationshipMessage['type']>([
  'relationship_letter_with_image',
  'relationship_monologue_slot',
  'relationship_event_slot',
  'relationship_romantic_moment',
  'tidal_hearts_video',
  'relationship_memory_album',
  'relationship_dream_slot',
  'relationship_gem_refund',
])

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value && typeof value === 'object')
}

// 为关系消息添加序列号和时间戳
const enrichRelationshipMessage = (
  parsedContent: Record<string, unknown>,
  message: Record<string, unknown>
): RelationshipMessage => {
  return {
    ...parsedContent,
    sequence: message.sequence,
    timestamp: message.timestamp,
  } as RelationshipMessage
}

const hasUserImageMedia = (message: HistoryMessageWithUserImages) => {
  return Boolean(
    message.image_media_items?.length ||
    message.original_image_urls?.length ||
    message.image_sequence
  )
}

const toSequenceNumber = (sequence?: string | number) => {
  if (sequence === undefined || sequence === null) return null

  const num = Number(sequence)
  return Number.isFinite(num) ? num : null
}

const getUserImageCount = (message: HistoryMessageWithUserImages) => {
  return Math.max(
    message.image_media_items?.length || 0,
    message.original_image_urls?.length || 0,
    message.image_sequence ? 1 : 0,
    1
  )
}

const buildUserImagePlaceholderContent = (imageCount: number) => {
  const imagePlaceholderContent = Array.from(
    { length: imageCount },
    () => APP_ONLY_IMAGE_PLACEHOLDER
  ).join('\n')

  return imagePlaceholderContent
}

const getUserImageSequences = (message: HistoryMessageWithUserImages) => {
  const imageSequences = [
    ...(message.image_media_items
      ?.map((item) => toSequenceNumber(item.sequence))
      .filter((sequence): sequence is number => sequence !== null) || []),
  ]
  const fallbackImageSequence = toSequenceNumber(message.image_sequence)
  if (
    fallbackImageSequence !== null &&
    !imageSequences.includes(fallbackImageSequence)
  ) {
    imageSequences.push(fallbackImageSequence)
  }

  return imageSequences
}

const buildCombinedUserImageContent = (
  message: HistoryMessageWithUserImages
) => {
  const textContent = message.content.trim()
  const imagePlaceholderContent = buildUserImagePlaceholderContent(
    getUserImageCount(message)
  )

  if (!textContent) return imagePlaceholderContent

  const messageSequence = toSequenceNumber(message.sequence)
  const imageSequences = getUserImageSequences(message)

  const shouldShowImageFirst =
    messageSequence === null ||
    !imageSequences.length ||
    imageSequences.some((sequence) => sequence <= messageSequence)

  return shouldShowImageFirst
    ? `${imagePlaceholderContent}\n\n${textContent}`
    : `${textContent}\n\n${imagePlaceholderContent}`
}

const stripUserImageFields = (message: HistoryMessageWithUserImages) => {
  const nextMessage = { ...message }
  delete nextMessage.app_only_image_placeholder
  delete nextMessage.image_media_items
  delete nextMessage.original_image_urls
  delete nextMessage.image_sequence
  return nextMessage
}

const splitUserImageMessage = (message: unknown): unknown[] => {
  if (!isRecord(message)) return [message]
  if (message.sender_type !== 'user') return [message]
  if (typeof message.content !== 'string') return [message]
  const imageMessage = message as HistoryMessageWithUserImages
  if (!hasUserImageMedia(imageMessage)) return [message]

  const baseMessage = stripUserImageFields(imageMessage)
  const textContent = imageMessage.content.trim()
  const messageSequence = toSequenceNumber(imageMessage.sequence)
  const imageSequences = getUserImageSequences(imageMessage)
  const splitImageSequences = imageSequences.filter(
    (sequence) => messageSequence === null || sequence !== messageSequence
  )

  if (!splitImageSequences.length) {
    return [
      {
        ...baseMessage,
        content: buildCombinedUserImageContent(imageMessage),
        content_type: 'text',
        ...(!textContent ? { app_only_image_placeholder: true } : {}),
      },
    ]
  }

  const imageMessages = splitImageSequences.map((sequence) => ({
    ...baseMessage,
    sequence: String(sequence),
    content: APP_ONLY_IMAGE_PLACEHOLDER,
    content_type: 'text',
    app_only_image_placeholder: true,
  }))

  const splitMessages = textContent
    ? [
        ...imageMessages,
        {
          ...baseMessage,
          content: textContent,
          content_type: 'text',
        },
      ]
    : imageMessages

  return splitMessages.sort((a, b) => {
    const aSequence = toSequenceNumber(a.sequence)
    const bSequence = toSequenceNumber(b.sequence)

    if (aSequence === null || bSequence === null) return 0
    return aSequence - bSequence
  })
}

const normalizeHistoryMessage = (message: unknown): unknown[] => {
  const messages = splitUserImageMessage(message)
  return messages.map(processMessageContent)
}

const normalizeHistoryMessages = (messages: unknown[]): unknown[] => {
  const normalizedMessages = messages.flatMap(normalizeHistoryMessage)
  const hasSplitMessage = normalizedMessages.length !== messages.length

  if (!hasSplitMessage) {
    return normalizedMessages
  }

  return normalizedMessages.sort((a, b) => {
    if (!isRecord(a) || !isRecord(b)) return 0

    const aSequence = toSequenceNumber(a.sequence as string | number)
    const bSequence = toSequenceNumber(b.sequence as string | number)

    if (aSequence === null || bSequence === null) return 0
    return aSequence - bSequence
  })
}

// 处理消息内容中的 JSON 序列化数据
const processMessageContent = (
  message: unknown
): Message | RelationshipMessage | unknown => {
  if (!isRecord(message)) {
    return message
  }

  // 检查是否是 Message 类型
  if (typeof message.content === 'string') {
    try {
      // 尝试解析 content 中的 JSON
      const parsedContent = JSON.parse(message.content) as Record<
        string,
        unknown
      >

      // 如果是关系消息类型，增强并返回
      if (
        relationshipMessageTypes.has(
          parsedContent.type as RelationshipMessage['type']
        )
      ) {
        return enrichRelationshipMessage(parsedContent, message)
      }
    } catch {
      // content 不是 JSON，保持原样
    }
  }

  return message
}

// 处理消息数组
const processMessages = (data: unknown[]): unknown[] => {
  return normalizeHistoryMessages(data)
}

export class AxiosRespError extends Error {
  response?: AxiosResponse<any, any>

  constructor(message: string, response?: AxiosResponse<any, any>) {
    super(message)
    this.response = response
    // 维护原型链
    Object.setPrototypeOf(this, AxiosRespError.prototype)
  }
}

export class TokenExpiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TokenExpiredError'
  }
}

let authFailureHandled = false

export const resetAuthFailureState = () => {
  authFailureHandled = false
}

const handleAuthFailure = (skipLoginModal = false, url = '') => {
  // [login-debug] 鉴权失败统一入口上报：用于定位「登录后被随后的鉴权失败踢下线」。
  // 覆盖两条触发路径——响应 401，以及失效态下请求拦截器的直接拦截。
  // 注意：这是链路外节点，funnel_sid 多为空/失真，靠 step + url 排查。
  trackLoginDebug('auth_failure', { url, firstHandle: !authFailureHandled })

  if (!authFailureHandled) {
    authFailureHandled = true
    signOut().catch()
  }

  if (!skipLoginModal) {
    setRecoil(loginModalOpenState, true)
  }
}

export const axiosPublic = axios.create({
  withCredentials: true,
})

axiosPublic.interceptors.request.use(
  (config) => {
    // 数据来源识别
    config.headers.Platform = 'web'
    // 预取用户隔离：带当前 user_id（登出态为空串）。与 boot 预热侧同逻辑，使 SW 的
    // dedupKey 两侧一致；登出/换号后值变化，旧预热自然 miss，杜绝串数据。
    // 仅在 SW 已接管页面（controller 非空、请求必被 SW 拦截剥掉此 header）时才注入——
    // 否则首屏/无 SW 场景这个自定义跨域 header 会直达后端、触发 CORS 预检（需后端白名单）。
    if (
      typeof navigator !== 'undefined' &&
      navigator.serviceWorker?.controller
    ) {
      config.headers[USER_ID_HEADER] = readUserId()
    }

    const token = getAuthToken()

    // Local token is a legacy optional header; cookie auth is authoritative.
    // Let the first unauthenticated request reach the server and handle 401 centrally.
    if (token && hasJwtNotExpired(token)) {
      config.headers.token = token
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosPublic.interceptors.response.use(
  (response) => {
    if (response?.data?.code !== 0) {
      const error = new AxiosRespError(
        response.data.msg || 'Unknown error',
        response
      )
      return Promise.reject(error)
    }
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const axiosAuth = axios.create({
  withCredentials: true,
})

axiosAuth.interceptors.request.use(
  (config) => {
    // 数据来源识别
    config.headers.Platform = 'web'
    // 预取用户隔离：带当前 user_id（与 axiosPublic / boot 同逻辑）。
    // 同 axiosPublic：仅在 SW 已接管页面（controller 非空）时注入，避免自定义 header
    // 在首屏/无 SW 场景直达后端触发 CORS 预检。
    if (
      typeof navigator !== 'undefined' &&
      navigator.serviceWorker?.controller
    ) {
      config.headers[USER_ID_HEADER] = readUserId()
    }

    if (authFailureHandled) {
      handleAuthFailure(config.skipLoginModalOn401, config.url ?? '')
      return Promise.reject(new TokenExpiredError('Token expired or missing'))
    }

    const token = getAuthToken()

    if (token && hasJwtNotExpired(token)) {
      config.headers.token = token
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)
axiosAuth.interceptors.response.use(
  (response) => {
    const user = getRecoil(userState)
    // 聊天相关宝石不足时弹窗
    if (response?.data?.code === AppRespCode.CodeNotEnoughGemsAmount) {
      if (chatUrls.includes(response.config.url || '')) {
        if (
          Number(user?.user_id) / 1000000000 + 3 * 86400 >
          Date.now() / 1000
        ) {
          setRecoil(newUserGemNotEnoughModalOpenState, true)
        } else {
          setRecoil(gemNotEnoughModalOpenState, true)
        }
      }
    }
    if (
      response?.data?.code === AppRespCode.CodeNotEnoughGemsAmount &&
      [
        `${BASE_API_URL}/character/generate_image_v2`,
        `${BASE_API_URL}/chat/media/image`,
        `${BASE_API_URL}/chat/media/video`,
      ].includes(response.config.url || '')
    ) {
      setRecoil(gemNotEnoughModalOpenState, true)
    }
    // 生成长视频时宝石不足时弹窗
    if (
      (response?.data?.code === AppRespCode.CodeNotEnoughMembershipRights ||
        response?.data?.code === AppRespCode.CodeNotEnoughGemsAmount) &&
      [
        `${BASE_API_URL}/chat/media/longVideo`,
        `${BASE_API_URL}/chat/media/longVideo/custom`,
      ].includes(response.config.url || '')
    ) {
      setRecoil(gemNotEnoughModalOpenState, true)
    }
    // role card超出弹窗
    if (response?.data?.code === 9) {
      if (roleCardUrls.includes(response.config.url || '')) {
        setRecoil(roleCardLimitModalOpenState, true)
      }
    }
    // 三叶草(幸运蛋)库存不足时弹窗；鸡尾酒(cocktail)的 16 由 CocktailButton 组件自行处理(需角色名)
    if (response?.data?.code === AppRespCode.CodeNotEnoughThreeLeafClover) {
      if (
        response.config.url ===
        `${BASE_API_URL}/user/character/relationship/gift/send`
      ) {
        const giftCode = (() => {
          try {
            return JSON.parse(response.config.data || '{}')?.gift_code as
              | string
              | undefined
          } catch {
            return undefined
          }
        })()
        if (giftCode === EASTER_CHAT_GIFT_CODES.LUCKY_EGG) {
          setRecoil(luckyEasterEggNotEnoughModalOpenState, true)
        }
      }
    }
    if (response?.data?.code !== 0) {
      const error = new AxiosRespError(
        response.data.msg || 'Unknown error',
        response
      )
      return Promise.reject(error)
    }

    // 只在 chat/history 接口处理消息内容中的 JSON 序列化数据和用户图片兼容展示
    const chatHistoryUrl = `${BASE_API_URL}/chat/history`
    if (
      response.config.url === chatHistoryUrl &&
      Array.isArray(response.data.data)
    ) {
      response.data.data = processMessages(response.data.data)
    }

    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      handleAuthFailure(
        error.config?.skipLoginModalOn401,
        error.config?.url ?? ''
      )
    }
    return Promise.reject(error)
  }
)
